import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  LinearProgress,
  Chip,
  Paper,
  IconButton,
  Fade,
  CircularProgress
} from '@mui/material';
import {
  SmartToy,
  Send,
  CheckCircle,
  Edit,
  Close
} from '@mui/icons-material';
import axios from 'axios';
import { brand } from '../../theme/colors';
import DialogCloseButton from '../common/DialogCloseButton';

/**
 * AI-Powered Simple Verification Request Dialog
 * Conversational interface for creating verification requests
 */
const AISimpleVerificationRequestDialog = ({ open, onClose, projectId, milestoneId, onSuccess }) => {
  const [step, setStep] = useState('conversation'); // conversation, review, creating
  const [conversationHistory, setConversationHistory] = useState([
    {
      role: 'assistant',
      content: "Hi! I'll help you request a quick verification. What do you need verified?"
    }
  ]);
  const [userInput, setUserInput] = useState('');
  const [generatedChecklist, setGeneratedChecklist] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);

  // Handle user message
  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const newMessage = { role: 'user', content: userInput };
    const updatedHistory = [...conversationHistory, newMessage];
    setConversationHistory(updatedHistory);
    setUserInput('');
    setIsTyping(true);

    try {
      // Check if we have enough information to generate checklist
      if (updatedHistory.filter(m => m.role === 'user').length >= 2) {
        // Generate checklist
        const response = await axios.post('/api/simple-verifications/ai/generate-checklist', {
          conversationHistory: updatedHistory,
          projectType: 'residential' // TODO: Get from project
        });

        setGeneratedChecklist(response.data.data);
        setStep('review');
      } else {
        // Continue conversation
        const aiResponse = getNextQuestion(updatedHistory);
        setConversationHistory([...updatedHistory, {
          role: 'assistant',
          content: aiResponse
        }]);
      }
    } catch (err) {
      setError('Failed to process your request. Please try again.');
      console.error(err);
    } finally {
      setIsTyping(false);
    }
  };

  // Get next AI question based on conversation
  const getNextQuestion = (history) => {
    const userMessages = history.filter(m => m.role === 'user');
    
    if (userMessages.length === 1) {
      return "Great! When do you need this verification completed?";
    } else if (userMessages.length === 2) {
      return "Perfect! Any specific concerns or requirements I should know about?";
    }
    
    return "Let me create a verification checklist for you...";
  };

  // Handle checklist edit
  const handleEditChecklist = () => {
    setStep('conversation');
    setConversationHistory([
      ...conversationHistory,
      {
        role: 'assistant',
        content: "Sure! What would you like to change about the verification?"
      }
    ]);
  };

  // Create verification request
  const handleCreateRequest = async () => {
    setStep('creating');
    
    try {
      const response = await axios.post('/api/simple-verifications/create', {
        projectId,
        milestoneId,
        verificationType: generatedChecklist.verificationType,
        title: generatedChecklist.title,
        description: generatedChecklist.description,
        checklist: generatedChecklist.checklist,
        requiredBy: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
        cost: generatedChecklist.estimatedCost,
        aiGeneratedChecklist: JSON.stringify(conversationHistory),
        aiEstimatedDuration: generatedChecklist.estimatedDuration
      });

      if (response.data.success) {
        onSuccess(response.data.data);
        onClose();
      }
    } catch (err) {
      setError('Failed to create verification request. Please try again.');
      console.error(err);
      setStep('review');
    }
  };

  // Reset dialog
  const handleClose = () => {
    setStep('conversation');
    setConversationHistory([
      {
        role: 'assistant',
        content: "Hi! I'll help you request a quick verification. What do you need verified?"
      }
    ]);
    setUserInput('');
    setGeneratedChecklist(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: brand.gradient,
          p: 0.5
        }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      <Box sx={{ background: 'white', borderRadius: 2.5 }}>
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy sx={{ color: brand.primary, fontSize: 28 }} />
            <Typography variant="h6" fontWeight="600">
              AI Verification Request
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {step === 'conversation' && (
            <Box>
              {/* Conversation History */}
              <Box sx={{ 
                height: 400, 
                overflowY: 'auto', 
                mb: 2,
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 2
              }}>
                {conversationHistory.map((message, index) => (
                  <Fade in key={index} timeout={300}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                        mb: 2
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          maxWidth: '70%',
                          bgcolor: message.role === 'user' ? brand.primary : 'white',
                          color: message.role === 'user' ? 'white' : 'black',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="body2">{message.content}</Typography>
                      </Paper>
                    </Box>
                  </Fade>
                ))}
                
                {isTyping && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    <SmartToy sx={{ color: brand.primary, fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      AI is thinking...
                    </Typography>
                    <CircularProgress size={16} />
                  </Box>
                )}
              </Box>

              {/* Input Field */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Type your response..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  disabled={isTyping}
                  variant="outlined"
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!userInput.trim() || isTyping}
                  sx={{
                    background: brand.gradient,
                    minWidth: 50
                  }}
                >
                  <Send />
                </Button>
              </Box>

              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          )}

          {step === 'review' && generatedChecklist && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Review Verification Request
              </Typography>

              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="600">
                      {generatedChecklist.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {generatedChecklist.description}
                    </Typography>
                  </Box>
                  <Chip 
                    label={generatedChecklist.verificationType.toUpperCase()} 
                    size="small"
                    sx={{ bgcolor: brand.primary, color: 'white' }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Checklist ({generatedChecklist.checklist.length} items)
                  </Typography>
                  {generatedChecklist.checklist.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {item.item}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.guidance}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Estimated Cost
                    </Typography>
                    <Typography variant="h6" color="primary" fontWeight="600">
                      ${generatedChecklist.estimatedCost}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Estimated Time
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {generatedChecklist.estimatedDuration} mins
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Button
                startIcon={<Edit />}
                onClick={handleEditChecklist}
                variant="outlined"
                fullWidth
                sx={{ mb: 1 }}
              >
                Make Changes
              </Button>
            </Box>
          )}

          {step === 'creating' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Creating Verification Request...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Notifying nearby verifiers
              </Typography>
            </Box>
          )}
        </DialogContent>

        {step === 'review' && (
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleCreateRequest}
              variant="contained"
              sx={{
                background: brand.gradient
              }}
            >
              Create Request
            </Button>
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
};

export default AISimpleVerificationRequestDialog;
